import React, { useState, useEffect, useContext } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { Search, Filter, ChevronDown, ChevronUp, AlertCircle, Package, Check, Truck, Archive, HelpCircle } from 'lucide-react';
import { AuthContext } from '../App';
import AppHeader from '../components/AppHeader';

const Purchasing = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, logout } = useContext(AuthContext);
  const [activeTab, setActiveTab] = useState('needs_purchasing');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedItems, setSelectedItems] = useState([]);
  const [expandedItems, setExpandedItems] = useState({});
  const [stats, setStats] = useState({
    needs_purchasing: 0,
    purchased: 0,
    received: 0,
    in_stock: 0
  });

  const tabs = [
    { id: 'needs_purchasing', label: 'Needs Purchasing', icon: Package },
    { id: 'purchased', label: 'Purchased', icon: Check },
    { id: 'received', label: 'Received', icon: Truck },
    { id: 'in_stock', label: 'In Stock', icon: Archive }
  ];

  // Check for tab in URL on mount
  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab && tabs.some(t => t.id === tab)) {
      setActiveTab(tab);
    }
  }, [searchParams]);

  useEffect(() => {
    fetchItems();
    fetchStats();
  }, [activeTab]);

  const fetchItems = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`/purchasing?status=${activeTab}&search=${searchQuery}`);
      setItems(response.data);
    } catch (error) {
      console.error('Failed to fetch purchasing items:', error);
      // Use mock data for demonstration
      setItems(getMockItems(activeTab));
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get('/purchasing/stats');
      setStats(response.data);
    } catch (error) {
      // Use mock stats
      setStats({
        needs_purchasing: 2632,
        purchased: 145,
        received: 89,
        in_stock: 1205
      });
    }
  };

  const getMockItems = (status) => {
    const mockItems = [
      {
        id: '1',
        product_name: 'Unisex Ideal Heavyweight Long-Sleeve T-Shirt',
        color: 'BLACK',
        brand: 'Next Level',
        style: '1801NL',
        supplier: 'alphabroder',
        total_qty: 24,
        sizes: { S: 4, M: 8, L: 8, XL: 4 },
        has_unpaid_order: true,
        status: status,
        image_url: 'https://www.ssactivewear.com/Images/Style/90038_f_fm.jpg'
      },
      {
        id: '2',
        product_name: 'Bella+Canvas Unisex Jersey Short-Sleeve T-Shirt',
        color: 'ASH',
        brand: 'Bella',
        style: '3001C',
        supplier: 'alphabroder',
        total_qty: 1,
        sizes: { M: 1 },
        has_unpaid_order: false,
        status: status,
        image_url: 'https://www.ssactivewear.com/Images/Style/16_f_fm.jpg'
      },
      {
        id: '3',
        product_name: 'Bella+Canvas Unisex Jersey Short-Sleeve T-Shirt',
        color: 'NATURAL',
        brand: 'Bella',
        style: '3001C',
        supplier: 'alphabroder',
        total_qty: 25,
        sizes: { S: 5, M: 8, L: 8, XL: 4 },
        has_unpaid_order: false,
        status: status,
        image_url: 'https://www.ssactivewear.com/Images/Style/16_f_fm.jpg'
      },
      {
        id: '4',
        product_name: 'Bella+Canvas Unisex Jersey Short-Sleeve T-Shirt',
        color: 'VINTAGE BLACK',
        brand: 'Bella',
        style: '3001C',
        supplier: 'alphabroder',
        total_qty: 25,
        sizes: { S: 5, M: 8, L: 8, XL: 4 },
        has_unpaid_order: false,
        status: status,
        image_url: 'https://www.ssactivewear.com/Images/Style/16_f_fm.jpg'
      },
      {
        id: '5',
        product_name: 'Gildan Heavy Cotton T-Shirt',
        color: 'RED',
        brand: 'Gildan',
        style: '5000',
        supplier: 'S&S Activewear',
        total_qty: 50,
        sizes: { S: 10, M: 15, L: 15, XL: 10 },
        has_unpaid_order: false,
        status: status,
        image_url: 'https://www.ssactivewear.com/Images/Style/16_f_fm.jpg'
      }
    ];
    return mockItems;
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchItems();
  };

  const toggleSelectAll = () => {
    if (selectedItems.length === items.length) {
      setSelectedItems([]);
    } else {
      setSelectedItems(items.map(item => item.id));
    }
  };

  const toggleSelectItem = (itemId) => {
    if (selectedItems.includes(itemId)) {
      setSelectedItems(selectedItems.filter(id => id !== itemId));
    } else {
      setSelectedItems([...selectedItems, itemId]);
    }
  };

  const toggleExpandItem = (itemId) => {
    setExpandedItems(prev => ({
      ...prev,
      [itemId]: !prev[itemId]
    }));
  };

  const updateItemStatus = async (newStatus) => {
    if (selectedItems.length === 0) {
      toast.error('Please select items first');
      return;
    }

    try {
      await axios.post('/purchasing/update-status', {
        item_ids: selectedItems,
        status: newStatus
      });
      toast.success(`${selectedItems.length} item(s) moved to ${newStatus.replace('_', ' ')}`);
      setSelectedItems([]);
      fetchItems();
      fetchStats();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const filteredItems = items.filter(item => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      item.product_name.toLowerCase().includes(query) ||
      item.brand.toLowerCase().includes(query) ||
      item.style.toLowerCase().includes(query) ||
      item.color.toLowerCase().includes(query)
    );
  });

  return (
    <div style={{ minHeight: '100vh', background: '#f8f9fa' }} data-testid="purchasing-page">
      {/* Header - Shared AppHeader Component */}
      <AppHeader />

      {/* Purchasing Sub-Header with Tabs */}
      <div style={{
        background: 'linear-gradient(135deg, #ff7f50 0%, #ff6347 100%)',
        padding: '12px 24px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '24px' }}>
          <h1 style={{ color: 'white', fontSize: '20px', fontWeight: '600', margin: 0, fontStyle: 'italic' }}>
            Purchasing
          </h1>
          
          {/* Tabs */}
          <div style={{ display: 'flex', gap: '8px' }}>
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                style={{
                  padding: '8px 16px',
                  borderRadius: '4px',
                  border: 'none',
                  background: activeTab === tab.id ? 'rgba(255,255,255,0.25)' : 'transparent',
                  color: 'white',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: activeTab === tab.id ? '600' : '400',
                  transition: 'all 0.2s'
                }}
                data-testid={`tab-${tab.id}`}
              >
                {tab.label} ({stats[tab.id] || 0})
              </button>
            ))}
          </div>
        </div>
        
        <button style={{
          background: 'transparent',
          border: 'none',
          color: 'white',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          fontSize: '14px'
        }}>
          <span style={{ 
            width: '20px', 
            height: '20px', 
            borderRadius: '50%', 
            border: '2px solid white',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '12px'
          }}>?</span>
          Help
        </button>
      </div>

      {/* Content */}
      <div style={{ padding: '24px', maxWidth: '1400px', margin: '0 auto' }}>
        {/* Title and Search Row */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '20px'
        }}>
          <h2 style={{ fontSize: '24px', fontWeight: '400', margin: 0, color: '#333' }}>
            {tabs.find(t => t.id === activeTab)?.label}
          </h2>
          
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <form onSubmit={handleSearch} style={{ position: 'relative', width: '400px' }}>
              <Search size={18} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }} />
              <Input
                type="text"
                placeholder="Search"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{ paddingLeft: '40px', width: '100%' }}
                data-testid="search-input"
              />
            </form>
            
            <button style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '10px 16px',
              background: 'white',
              border: '1px solid #e5e7eb',
              borderRadius: '6px',
              cursor: 'pointer',
              color: '#374151',
              fontSize: '14px'
            }}>
              <Filter size={16} /> FILTER
            </button>
          </div>
        </div>

        {/* Action Buttons (when items selected) */}
        {selectedItems.length > 0 && (
          <div style={{
            background: '#1a1a2e',
            padding: '12px 20px',
            borderRadius: '8px',
            marginBottom: '16px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            <span style={{ color: 'white', fontSize: '14px' }}>
              {selectedItems.length} item(s) selected
            </span>
            <div style={{ display: 'flex', gap: '8px' }}>
              {activeTab === 'needs_purchasing' && (
                <Button size="sm" onClick={() => updateItemStatus('purchased')} style={{ background: '#3b82f6' }}>
                  Mark as Purchased
                </Button>
              )}
              {activeTab === 'purchased' && (
                <Button size="sm" onClick={() => updateItemStatus('received')} style={{ background: '#8b5cf6' }}>
                  Mark as Received
                </Button>
              )}
              {activeTab === 'received' && (
                <Button size="sm" onClick={() => updateItemStatus('in_stock')} style={{ background: '#16a34a' }}>
                  Move to Stock
                </Button>
              )}
              <Button size="sm" variant="outline" onClick={() => setSelectedItems([])} style={{ color: 'white', borderColor: 'white' }}>
                Clear Selection
              </Button>
            </div>
          </div>
        )}

        {/* Select All */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          padding: '12px 16px',
          background: 'white',
          borderBottom: '1px solid #e5e7eb'
        }}>
          <Checkbox
            checked={selectedItems.length === items.length && items.length > 0}
            onCheckedChange={toggleSelectAll}
            data-testid="select-all-checkbox"
          />
          <span style={{ color: '#374151', fontSize: '14px' }}>Select All</span>
        </div>

        {/* Items List */}
        <div style={{ background: 'white', borderRadius: '0 0 8px 8px' }}>
          {loading ? (
            <div style={{ padding: '40px', textAlign: 'center', color: '#6b7280' }}>
              Loading...
            </div>
          ) : filteredItems.length === 0 ? (
            <div style={{ padding: '40px', textAlign: 'center', color: '#6b7280' }}>
              No items found
            </div>
          ) : (
            filteredItems.map((item, index) => (
              <div
                key={item.id}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  padding: '20px',
                  borderBottom: index < filteredItems.length - 1 ? '1px solid #e5e7eb' : 'none',
                  gap: '16px'
                }}
                data-testid={`purchasing-item-${item.id}`}
              >
                {/* Checkbox */}
                <Checkbox
                  checked={selectedItems.includes(item.id)}
                  onCheckedChange={() => toggleSelectItem(item.id)}
                />
                
                {/* Product Image */}
                <img
                  src={item.image_url || `https://via.placeholder.com/80x100?text=${item.style}`}
                  alt={item.product_name}
                  style={{
                    width: '60px',
                    height: '80px',
                    objectFit: 'cover',
                    borderRadius: '4px',
                    background: '#f3f4f6'
                  }}
                  onError={(e) => e.target.src = `https://via.placeholder.com/60x80?text=${item.style}`}
                />
                
                {/* Product Details */}
                <div style={{ flex: 1 }}>
                  <h3 style={{ margin: '0 0 4px 0', fontSize: '16px', fontWeight: '500', color: '#1f2937' }}>
                    {item.product_name} - {item.color}
                  </h3>
                  <p style={{ margin: '0 0 4px 0', fontSize: '14px', color: '#6b7280' }}>
                    {item.brand} | {item.style}
                  </p>
                  <p style={{ margin: '0 0 8px 0', fontSize: '14px', color: '#9ca3af' }}>
                    {item.supplier}
                  </p>
                  
                  {/* Warning Badge */}
                  {item.has_unpaid_order && (
                    <div style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '6px',
                      padding: '6px 12px',
                      background: '#fef3c7',
                      borderRadius: '4px',
                      fontSize: '13px',
                      color: '#92400e'
                    }}>
                      <AlertCircle size={14} />
                      One or more items belong to an unpaid order.
                    </div>
                  )}
                  
                  {/* Size Breakdown (expandable) */}
                  {expandedItems[item.id] && (
                    <div style={{
                      marginTop: '12px',
                      padding: '12px',
                      background: '#f9fafb',
                      borderRadius: '6px'
                    }}>
                      <p style={{ margin: '0 0 8px 0', fontSize: '13px', fontWeight: '600', color: '#374151' }}>
                        Size Breakdown:
                      </p>
                      <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                        {Object.entries(item.sizes).map(([size, qty]) => (
                          <div key={size} style={{ textAlign: 'center' }}>
                            <div style={{ fontSize: '12px', color: '#6b7280' }}>{size}</div>
                            <div style={{ fontSize: '16px', fontWeight: '600', color: '#1f2937' }}>{qty}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                
                {/* Quantity & Actions */}
                <div style={{ textAlign: 'right', minWidth: '120px' }}>
                  <p style={{ margin: '0 0 4px 0', fontSize: '14px', color: '#374151' }}>
                    Total Qty: <strong>{item.total_qty}</strong>
                  </p>
                  <button
                    onClick={() => toggleExpandItem(item.id)}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: '#3b82f6',
                      cursor: 'pointer',
                      fontSize: '14px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                      marginLeft: 'auto'
                    }}
                  >
                    {expandedItems[item.id] ? (
                      <>Hide Sizes <ChevronUp size={14} /></>
                    ) : (
                      <>Show Sizes <ChevronDown size={14} /></>
                    )}
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default Purchasing;
